import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ServiceCaseListDto } from '../../../../core/models/customer-service/customer-service-case.model';
import { CustomerServiceCaseMapper } from '../../../../core/mappers/customer-service/mapper-customer-service-case.service';
import { MapperAccessUsersService } from '../../../../core/mappers/system-settings/mapper-access-users.service';
import { AccessUserDetailDto } from '../../../../core/models/system-settings/access-users.model';
import { ApiCustomerServiceCaseService } from '../../../../core/services/api/customer-service/api-customer-service-case.service';
import { ApiCustomerServiceIssueTypeService } from '../../../../core/services/api/customer-service/api-customer-service-issue-type.service';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalCustomerServiceCaseComponent } from '../../modals/customer-services/customer-service-case/modal-customer-service-case/modal-customer-service-case.component';
import { ModalCustomerServiceCaseDetailComponent } from '../../modals/customer-services/customer-service-case/modal-customer-service-case-detail/modal-customer-service-case-detail.component';

@Component({
  selector: 'app-customer-service-case',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    ModalCustomerServiceCaseComponent,
    ModalCustomerServiceCaseDetailComponent
  ],
  templateUrl: './customer-service-case.component.html',
  styleUrl: './customer-service-case.component.css'
})
export class CustomerServiceCaseComponent implements OnInit {
  private readonly apiCase = inject(ApiCustomerServiceCaseService);
  private readonly mapper = inject(CustomerServiceCaseMapper);
  private readonly mapperAccessUsers = inject(MapperAccessUsersService);
  private readonly apiIssueType = inject(ApiCustomerServiceIssueTypeService);
  private readonly apiSystem = inject(ApiSystemSettingsService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly siteState = inject(SiteStateService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  // ================= State Variables =================
  isLoading = false;
  cases: ServiceCaseListDto[] = [];
  userId = 0;

  // --- Filtering Options ---
  showTableFilters = false;

  siteId: number | null = null;
  searchSiteText = '';
  isSiteDropdownOpen = false;

  status: string | null = null;
  issueTypeId: number | null = null;
  assignedUserId: number | null = null;

  startDate: Date | null = null;
  endDate: Date | null = null;

  searchText = '';
  searchField = 'all';
  isSearchTypeDropdownOpen = false;

  // --- Sorting ---
  sortColumn = 'createDate';
  sortDirection: 'asc' | 'desc' | '' = 'desc';

  // --- Pagination ---
  currentPage = 1;
  pageSize = 20;
  pageSizeOptions = [20, 50, 100];
  totalItems = 0;
  totalPages = 0;
  isPageSizeDropdownOpen = false;

  // --- Dropdown options ---
  sitesList: Array<{ id: number; name: string }> = [];
  issueTypes: Array<{ id: number; name: string }> = [];
  staffList: Array<{ id: number; name: string }> = [];

  // --- Modals control ---
  isCreateModalOpen = false;
  isDetailModalOpen = false;
  selectedCaseId: number | null = null;

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get filteredSites(): Array<{ id: number; name: string }> {
    const q = this.searchSiteText.trim().toLowerCase();
    if (!q) return this.sitesList;
    return this.sitesList.filter((s) => s.name.toLowerCase().includes(q));
  }

  get selectedSiteLabel(): string {
    if (this.siteId == null) return 'ทั้งหมด (ตามสิทธิ์)';
    return this.sitesList.find((s) => s.id === this.siteId)?.name || 'ทั้งหมด (ตามสิทธิ์)';
  }

  // ================= Normal Functions =================

  ngOnInit(): void {
    this.userId = this.resolveUserId();
    // Default: all sites within user access (not forced to sidebar selection)
    this.siteId = null;
    this.searchSiteText = this.selectedSiteLabel;

    this.loadSites();
    this.loadDropdowns();
    this.loadCases();
  }

  onSearchChange(): void {
    this.currentPage = 1;
  }

  onClickSearch(): void {
    this.currentPage = 1;
    this.loadCases();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadCases();
  }

  toggleSiteDropdown(): void {
    this.isSiteDropdownOpen = !this.isSiteDropdownOpen;
    if (this.isSiteDropdownOpen) {
      this.searchSiteText = '';
    } else {
      this.searchSiteText = this.selectedSiteLabel;
    }
  }

  selectSite(option: { id: number | null; name: string }): void {
    this.siteId = option.id;
    this.searchSiteText = option.name;
    this.isSiteDropdownOpen = false;
    this.onFilterChange();
  }

  closeSiteDropdown(): void {
    setTimeout(() => {
      this.isSiteDropdownOpen = false;
      if (!this.searchSiteText.trim()) {
        this.siteId = null;
        this.searchSiteText = 'ทั้งหมด (ตามสิทธิ์)';
        this.onFilterChange();
        return;
      }

      const exactMatch = this.sitesList.find(
        (s) => s.name.toLowerCase() === this.searchSiteText.trim().toLowerCase()
      );
      if (exactMatch) {
        if (this.siteId !== exactMatch.id) {
          this.siteId = exactMatch.id;
          this.searchSiteText = exactMatch.name;
          this.onFilterChange();
        } else {
          this.searchSiteText = exactMatch.name;
        }
        return;
      }

      const allLabels = ['ทั้งหมด (ตามสิทธิ์)', 'ทั้งหมด'];
      if (allLabels.includes(this.searchSiteText.trim())) {
        this.siteId = null;
        this.searchSiteText = 'ทั้งหมด (ตามสิทธิ์)';
        this.onFilterChange();
        return;
      }

      this.searchSiteText = this.selectedSiteLabel;
    }, 200);
  }

  onSiteSearchFocus(): void {
    this.isSiteDropdownOpen = true;
    this.searchSiteText = '';
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: string): void {
    this.searchField = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchField) {
      case 'caseno': return 'เลขที่เคส';
      case 'contactname': return 'ชื่อผู้ติดต่อ';
      case 'contactphone': return 'เบอร์โทรผู้ติดต่อ';
      case 'subject': return 'หัวข้อแจ้งเรื่อง';
      default: return 'ทั้งหมด';
    }
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadCases();
  }

  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  closePageSizeDropdown(): void {
    setTimeout(() => {
      this.isPageSizeDropdownOpen = false;
    }, 200);
  }

  selectPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.isPageSizeDropdownOpen = false;
    this.loadCases();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadCases();
    }
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getStatusLabel(status: string): string {
    return this.mapper.translateStatus(status);
  }

  getStatusColorClass(status: string): string {
    return this.mapper.getStatusColorClass(status);
  }

  getPriorityLabel(priority: string): string {
    return this.mapper.translatePriority(priority);
  }

  getPriorityColorClass(priority: string): string {
    return this.mapper.getPriorityColorClass(priority);
  }

  getChannelLabel(channel: string): string {
    return this.mapper.translateChannel(channel);
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์เปิดเคสประสานงาน');
      return;
    }
    this.isCreateModalOpen = true;
  }

  viewCaseDetail(caseId: number): void {
    this.selectedCaseId = caseId;
    this.isDetailModalOpen = true;
  }

  onSaved(): void {
    this.loadCases();
  }

  // ================= API Functions =================

  loadSites(): void {
    if (!this.userId) {
      this.userId = this.resolveUserId();
    }
    if (!this.userId) {
      this.sitesList = [];
      return;
    }

    this.apiSystem.getAccessUserById(this.userId).subscribe({
      next: (dto: AccessUserDetailDto) => {
        const sites = this.mapperAccessUsers.mapSitesFromDto(dto);
        this.siteState.setSites(sites);
        this.sitesList = sites.map((s) => ({ id: s.id, name: s.name }));
        this.searchSiteText = this.selectedSiteLabel;
      },
      error: () => {
        this.sitesList = [];
        this.toast.error('โหลดรายการลานจอดไม่สำเร็จ');
      },
    });
  }

  loadDropdowns(): void {
    this.apiIssueType.getIssueTypeDropdown(true).subscribe({
      next: (res) => {
        const list = res.data || res.Data || res || [];
        this.issueTypes = list.map((i: any) => ({
          id: i.id ?? i.Id,
          name: i.name ?? i.Name
        }));
      },
      error: () => (this.issueTypes = [])
    });

    this.apiSystem.getAccessUsersForTable(1, 200).subscribe({
      next: (res) => {
        const list = res.data || [];
        this.staffList = list.map((u: any) => ({
          id: u.id ?? u.Id,
          name: u.fullName ?? u.FullName ?? u.userName ?? u.UserName ?? '-'
        }));
      },
      error: () => (this.staffList = [])
    });
  }

  loadCases(): void {
    if (!this.userId) {
      this.userId = this.resolveUserId();
    }
    if (!this.userId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้');
      return;
    }

    this.isLoading = true;

    let startDateStr: string | null = null;
    if (this.startDate) {
      const d = new Date(this.startDate);
      d.setHours(0, 0, 0, 0);
      startDateStr = d.toISOString();
    }

    let endDateStr: string | null = null;
    if (this.endDate) {
      const d = new Date(this.endDate);
      d.setHours(23, 59, 59, 999);
      endDateStr = d.toISOString();
    }

    const query = {
      page: this.currentPage,
      pageSize: this.pageSize,
      userId: this.userId,
      siteId: this.siteId || undefined,
      status: this.status || undefined,
      issueTypeId: this.issueTypeId || undefined,
      assignedUserId: this.assignedUserId || undefined,
      startDate: startDateStr || undefined,
      endDate: endDateStr || undefined,
      searchField: this.searchText.trim() ? this.searchField : undefined,
      searchText: this.searchText.trim() || undefined
    };

    this.apiCase.getServiceCaseForTable(query).subscribe({
      next: (res) => {
        this.isLoading = false;
        const mapped = this.mapper.mapCaseTable(res);

        if (this.sortColumn) {
          mapped.data.sort((a: any, b: any) => {
            const valA = a[this.sortColumn];
            const valB = b[this.sortColumn];
            if (valA == null) return 1;
            if (valB == null) return -1;

            if (typeof valA === 'string') {
              return this.sortDirection === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
            } else {
              return this.sortDirection === 'asc'
                ? valA - valB
                : valB - valA;
            }
          });
        }

        this.cases = mapped.data;
        this.totalItems = mapped.total;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดข้อมูลรายการเคสปัญหาได้');
        console.error(err);
        this.cases = [];
      }
    });
  }

  private resolveUserId(): number {
    const local = localStorage.getItem('userId');
    if (local) return Number(local) || 0;
    return Number(this.cookieService.get('user_id') || 0);
  }
}
